import React, { memo, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Form, useField } from 'react-final-form';
import { useDispatch } from 'react-redux';

import UsersTable from './users-table/UsersTable';
import AddUserForm from './add-user-from/AddUserForm';
import validate from '../../../utils/validate';
import { addCompanyUserFormYupValidation } from './validation';
import {
  addUserToCompany,
  adminUserCheck,
  enableUser,
  removeUserFromCompanyCreate,
} from '../../../redux/customers/customersActions';
import CustomerSection from '../customer-section/CustomerSection';
import WithError from '../../../components/with-error/WithError';
import { USER_ROLES } from '../../../constants';

import scss from './CompanyUsers.module.scss';

const CompanyUsers = ({ input, meta }) => {
  const { t } = useTranslation('customers');
  const dispatch = useDispatch();

  const assignedCompanyProjects = useField('assignedProjects').input.value;

  const handleAddUser = useCallback(
    async (values, form) => {
      const res = await dispatch(addUserToCompany(values));

      if (res.payload) {
        input.onChange([...input.value, { ...res.payload, assignedProjects: values.assignedProjects }]);
        form.restart();
        Object.entries(addUserInitialValues).forEach(([key, value]) => form.change(key, value));
      }
    },
    [input, dispatch],
  );

  const handleRemoveUser = useCallback(
    (user) => {
      dispatch(removeUserFromCompanyCreate(user));
      const newCompanyUsers = input.value.filter(({ id }) => id !== user.id);
      input.onChange(newCompanyUsers);
    },
    [input, dispatch],
  );

  const handleEnableUser = useCallback(
    (user) => {
      dispatch(enableUser(user));

      const newCompanyUsers = input.value.map((existedUser) =>
        existedUser.id === user.id ? { ...user, enabled: !user.enabled } : existedUser,
      );
      input.onChange(newCompanyUsers);
    },
    [dispatch, input],
  );

  const handleAdminUser = useCallback(
    ({ roleName, id }) => {
      const newRole = roleName === USER_ROLES.ROLE_CUSTOMER ? USER_ROLES.COMPANY_ADMIN : USER_ROLES.ROLE_CUSTOMER;

      dispatch(adminUserCheck({ id, roleName: newRole }));

      const newCompanyUsers = input.value.map((existedUser) =>
        existedUser.id === id ? { ...existedUser, roleName: newRole } : existedUser,
      );
      input.onChange(newCompanyUsers);
    },
    [dispatch, input],
  );

  return (
    <div className={scss.wrapper} name={input.name}>
      <CustomerSection title={t('addUser')}>
        <WithError
          isShowError={meta.touched && meta.error}
          errorText={t(meta.error)}
          extraClassName={scss.sectionError}>
          <Form
            initialValues={addUserInitialValues}
            onSubmit={handleAddUser}
            subscription={{ submitting: true, pristine: true, submitError: true }}
            keepDirtyOnReinitialize
            validate={(values) => validate(values, addCompanyUserFormYupValidation())}
            render={({ handleSubmit }) => (
              <AddUserForm onSubmit={handleSubmit} assignedCompanyProjects={assignedCompanyProjects} />
            )}
          />
        </WithError>
      </CustomerSection>

      <UsersTable
        content={input.value}
        onUsersChange={input.onChange}
        onRemoveUsers={handleRemoveUser}
        onEnable={handleEnableUser}
        onAdminCheck={handleAdminUser}
        assignedCompanyProjects={assignedCompanyProjects}
      />
    </div>
  );
};

export default memo(CompanyUsers);

const addUserInitialValues = {
  firstName: '',
  lastName: '',
  username: '',
  password: '',
  assignedProjects: [],
};
